// Shared "rule config" one-liner for sales tasks, used by both the task form
// (below the student name) and the list page's Task column:
//
//   [Rule name] - [Day from last transition activity] - [generated date]
//   e.g. "No book - Day 1 - 17 july 2026"
//
// Day N is the rule's configured day offset (sales_task_list.rule_day) counted
// from the rule's anchor — the student's last transition activity (signup date
// for signup-anchored rules, the first booking for BOOK_NO_JOIN, the first
// attended session for JOIN_NO_MEMBERSHIP, the latest concierge-bucket entry
// for bucket-anchored ones). The server sends the
// per-rule wording as anchor_label, which the config UI renders verbatim.
// Day 0 = generated on the day of the transition, Day 1
// = the day after — rule_day drives when the task is generated, not when it is
// due (due_datetime is generation time + the rule's due-offset hours).
// The generated date is the task row's created date, rendered
// "17 july 2026" (lowercase month per the spec example).
//
// Some task types have no day offset at all — FRESH_LEAD,
// UNANSWERED_INCOMING_CALL, PRICING_PAGE_VISITED and THREE_MONTH_PLAN_OPENED
// are created by a student-lifecycle event, UPCOMING_TRIAL_SESSION by an imminent session and
// UNANSWERED_INCOMING_SMS by its own 15-minute sweep, not by a day-N rule — so the
// "Day N" segment is dropped for them and the line reads e.g.
// "Unanswered incoming call - 31 july 2026" or
// "Pricing page visited - 31 july 2026". The same is true of
// LEAD_OTP_NOT_VERIFIED (its own sweep, anchored on lead creation) and
// CHURNED_MEMBER_LOGIN (a login event).
import moment from 'moment-timezone';

// Task types created by an event, an imminent session or their own dedicated
// sweep rather than by a day-offset rule; "Day N" is meaningless for these.
const NO_DAY_TASK_TYPES = [
  'FRESH_LEAD',
  'UPCOMING_TRIAL_SESSION',
  'UNANSWERED_INCOMING_CALL',
  'UNANSWERED_INCOMING_SMS',
  'PRICING_PAGE_VISITED',
  'THREE_MONTH_PLAN_OPENED',
  // Both write rule_day 0 server-side because no day offset exists for them:
  // the lead sweep is anchored on lead creation plus a grace, and the churned
  // rule fires on the login itself.
  'LEAD_OTP_NOT_VERIFIED',
  'CHURNED_MEMBER_LOGIN',
  // Wave 7 friction rules: each is published by a funnel event (some after a
  // delayed re-check), never by a day offset, so they all write rule_day 0.
  // Mirrors TYPE_OPTIONS in filters.js.
  'LOGIN_ISSUE',
  'BOOKING_SKIPPED_ONBOARDING',
  'OTP_VERIFICATION_FAILED',
  'ONBOARDING_IDLE_TIMEOUT',
  'ONBOARDING_DROP_OFF',
  'SIGNUP_NOT_COMPLETED',
  'NO_BOOKING_SLOTS',
  'CHECKOUT_DROP_OFF',
  'SLOTS_VIEWED_NOT_BOOKED',
  'PRE_SESSION_NO_JOIN',
  'REMINDER_LINK_NO_JOIN',
  'REMINDER_REPLY',
  'BOOKING_ERROR'
];

const rule_config_line = (task_label, rule_day, created_date, task_type) => {
  const parts = [task_label || '—'];
  const has_day = !NO_DAY_TASK_TYPES.includes(task_type);
  if (has_day && rule_day !== undefined && rule_day !== null) parts.push(`Day ${rule_day}`);
  if (created_date) {
    const m = moment(created_date);
    if (m.isValid()) parts.push(m.format('D MMMM YYYY').toLowerCase());
  }
  return parts.join(' - ');
};

export default rule_config_line;
